/**
 * TransferHistory Component
 * 송금 관리 페이지 - 본사에서 협력사로 송금
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTransferData } from '../hooks/useTransferData';
import { useSortConfig } from "@/features/system/transfer/hooks/useSortConfig";
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import { usePagination } from '@/shared/hooks/usePagination';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import Loading from '@/shared/components/feedback/Loading';
import TransferModal from './TransferModal';
import FilterSection from './FilterSection';
import HistoryTable from './HistoryTable';
import SummarySection from './SummarySection';
import { TransferFilters } from "@/features/system/transfer/types/transfer.types";
import { createDefaultTransferFilters } from "@/features/system/transfer/utils/transferUtils";
import { TRANSFER_ITEMS_PER_PAGE, STORAGE_KEY_TRANSFER_FILTERS } from "@/features/system/transfer/constants/transfer.constants";
import { getItem, STORAGE_KEYS } from '@/shared/utils/storage';
import '@/shared/styles/common-table.css';
import '../styles/TransferHistory.css';

const TransferHistory: React.FC = () => {
    // ======== Hooks ========
    const {
        histories,
        totalTransferAmt,
        supplierOptions,
        loading,
        excelDownloading,
        fetchHistories,
        fetchSupplierOptions,
        requestTransfer,
        downloadExcel
    } = useTransferData();

    const showLoading = useDelayedLoading(loading);

    const { sortConfig, handleSort, sortedHistories } = useSortConfig(histories);

    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedHistories, itemsPerPage: TRANSFER_ITEMS_PER_PAGE });

    // ======== State ========
    /** 검색 필터 상태 */
    const [filters, setFilters] = useState<TransferFilters>(() => {
        const saved = loadFilters<TransferFilters>(STORAGE_KEY_TRANSFER_FILTERS);
        return saved || createDefaultTransferFilters();
    });

    /** 송금 모달 열림 상태 */
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

    /** 초기화 완료 여부 */
    const hasInitialized = useRef(false);

    // ======== Effects ========
    /** 초기 데이터 로드 */
    useEffect(() => {
        if (!hasInitialized.current) {
            hasInitialized.current = true;
            const savedFilters = loadFilters<TransferFilters>(STORAGE_KEY_TRANSFER_FILTERS);
            const filtersToUse = savedFilters || filters;

            fetchHistories(
                filtersToUse.startDate,
                filtersToUse.endDate,
                filtersToUse.suppId || undefined
            ).catch(() => { /* 에러는 훅에서 처리 */ });

            fetchSupplierOptions().catch(() => { /* 에러는 훅에서 처리 */ });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ======== Event Handlers ========
    /** 검색 실행 */
    const handleSearch = useCallback(() => {
        resetPagination();
        fetchHistories(
            filters.startDate,
            filters.endDate,
            filters.suppId || undefined
        );
        saveFilters(STORAGE_KEY_TRANSFER_FILTERS, filters);
    }, [filters, resetPagination, fetchHistories]);

    /** 송금 처리 */
    const handleRequestTransfer = useCallback(async (
        suppId: string,
        amount: number,
        memo?: string
    ) => {
        const result = await requestTransfer(suppId, amount, memo);
        if (result) {
            setIsTransferModalOpen(false);
            fetchHistories(
                filters.startDate,
                filters.endDate,
                filters.suppId || undefined
            );
        }
    }, [requestTransfer, filters, fetchHistories]);

    /** 송금 모달 열기 */
    const handleTransferModalOpen = useCallback(() => {
        setIsTransferModalOpen(true);
    }, []);

    const canTransfer = getItem<string>('PRM_CODE') === 'A';

    /** 엑셀 다운로드 */
    const handleExcelDownload = useCallback(() => {
        downloadExcel(
            filters.startDate,
            filters.endDate,
            filters.suppId || undefined
        );
    }, [filters, downloadExcel]);

    // ======== Render ========
    if (showLoading) {
        return <Loading />;
    }

    return (
        <div className="transfer-history">
            {/* 페이지 헤더 */}
            <div className="page-header">
                <h1 className="page-title">송금 관리</h1>
            </div>

            {/* 검색 필터 섹션 */}
            <FilterSection
                filters={filters}
                supplierOptions={supplierOptions}
                onFilterChange={setFilters}
                onSearch={handleSearch}
                onTransferClick={handleTransferModalOpen}
                onExcelDownload={handleExcelDownload}
                excelDownloading={excelDownloading}
                isTransferEnabled={canTransfer}
            />

            {/* 합계 섹션 */}
            <SummarySection
                totalCount={histories.length}
                totalTransferAmt={totalTransferAmt}
            />

            {/* 검색 결과 건수 */}
            <div className="result-count">
                <i className="fas fa-list"></i>
                총 <strong>{sortedHistories.length.toLocaleString()}건</strong>
            </div>

            {/* 송금 내역 테이블 */}
            <HistoryTable
                histories={sortedHistories}
                currentItems={currentItems}
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={TRANSFER_ITEMS_PER_PAGE}
                onPageChange={goToPage}
                sortConfig={sortConfig}
                onSort={handleSort}
            />

            {/* 송금 모달 */}
            {isTransferModalOpen && (
                <TransferModal
                    supplierOptions={supplierOptions}
                    onClose={() => setIsTransferModalOpen(false)}
                    onRequest={handleRequestTransfer}
                />
            )}
        </div>
    );
};

export default TransferHistory;
